import { Trans, t } from "@lingui/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCopyToClipboard, usePrevious } from "react-use";

import { useAffiliateCodes, useUserReferralCode } from "domain/referrals";
import { Token } from "domain/tokens";
import { shareOrCopyElementAsImage } from "lib/copyElementAsImage";
import { helperToast } from "lib/helperToast";
import { getTwitterIntentURL } from "lib/legacy";
import { useLocalStorageSerializeKey } from "lib/localStorage";
import { getShareURL, uploadElementAsShareImage } from "lib/shareImage";
import { useBreakpoints } from "lib/useBreakpoints";
import useLoadImage from "lib/useLoadImage";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionActionEvent, SharePositionActionSource } from "lib/userAnalytics/types";
import type { ContractsChainId } from "sdk/configs/chains";

import { AlertInfoCard } from "components/AlertInfo/AlertInfoCard";
import Button from "components/Button/Button";
import Checkbox from "components/Checkbox/Checkbox";
import { ColorfulBanner } from "components/ColorfulBanner/ColorfulBanner";
import ModalWithPortal from "components/Modal/ModalWithPortal";
import ToggleSwitch from "components/ToggleSwitch/ToggleSwitch";

import CopyStrokeIcon from "img/ic_copy_stroke.svg?react";
import InfoIcon from "img/ic_info.svg?react";
import ShareArrowOutlineIcon from "img/ic_share_arrow_outline.svg?react";
import TwitterIcon from "img/ic_x.svg?react";
import shareBgImg from "img/position-share-bg.jpg";

import CreateReferralCode from "./CreateReferralCode";
import { PositionShareCard } from "./PositionShareCard";

type Props = {
  entryPrice: bigint | undefined;
  indexToken: Token;
  isLong: boolean;
  leverageWithPnl: bigint | undefined;
  leverageWithoutPnl: bigint | undefined;
  markPrice: bigint;
  pnlAfterFeesPercentage: bigint;
  pnlAfterFeesUsd: bigint;
  setIsPositionShareModalOpen: (isOpen: boolean) => void;
  isPositionShareModalOpen: boolean;
  account: string | undefined;
  chainId: ContractsChainId;
  doNotShowAgain?: boolean;
  onDoNotShowAgainChange?: (value: boolean) => void;
  onShareAction?: () => void;
  shareSource: SharePositionActionSource;
  isRpnl?: boolean;
};

function PositionShare({
  entryPrice,
  indexToken,
  isLong,
  leverageWithoutPnl,
  leverageWithPnl,
  markPrice,
  pnlAfterFeesPercentage,
  pnlAfterFeesUsd,
  setIsPositionShareModalOpen,
  isPositionShareModalOpen,
  account,
  chainId,
  doNotShowAgain,
  onDoNotShowAgainChange,
  onShareAction,
  shareSource,
  isRpnl = false,
}: Props) {
  const userAffiliateCode = useAffiliateCodes(chainId, account);
  const { userReferralCodeString: usedReferralCode } = useUserReferralCode(chainId, account);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showPnlAmounts, setShowPnlAmounts] = useState(false);
  const [isPnlInLeverage, setIsPnlInLeverage] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const { isMobile } = useBreakpoints();
  const sharePositionBgImg = useLoadImage(shareBgImg);
  const cardRef = useRef<HTMLDivElement>(null);
  const [createdReferralCode, setCreatedReferralCode] = useState<string | null>(null);
  const [isCreateReferralCodeInfoMessageClosed, setIsCreateReferralCodeInfoMessageClosed] = useLocalStorageSerializeKey(
    "is-create-referral-code-info-message-closed",
    false
  );
  const shareAffiliateCode = useMemo(() => {
    if (createdReferralCode) {
      return { code: createdReferralCode, success: true };
    }
    return userAffiliateCode;
  }, [createdReferralCode, userAffiliateCode]);
  const hasReferralCode = Boolean(shareAffiliateCode?.code);

  const { referralCodeOwnerKind, code } = useMemo(() => {
    if (hasReferralCode && shareAffiliateCode?.code) {
      return { referralCodeOwnerKind: "created" as const, code: shareAffiliateCode.code };
    }
    if (usedReferralCode) {
      return { referralCodeOwnerKind: "used" as const, code: usedReferralCode };
    }
    return { referralCodeOwnerKind: undefined, code: undefined };
  }, [hasReferralCode, shareAffiliateCode?.code, usedReferralCode]);

  const [promptedToCreateReferralCode, setPromptedToCreateReferralCode] = useState(false);

  const prevIsOpen = usePrevious(isPositionShareModalOpen);

  // cache position on open to not upload new image on every position change
  const [cachedPositionData, setCachedPositionData] = useState<{
    entryPrice: bigint | undefined;
    indexToken: Token;
    isLong: boolean;
    leverageWithPnl: bigint | undefined;
    leverageWithoutPnl: bigint | undefined;
    markPrice: bigint;
    pnlAfterFeesPercentage: bigint;
    pnlAfterFeesUsd: bigint;
  } | null>(null);

  useEffect(() => {
    if (!prevIsOpen && isPositionShareModalOpen) {
      setCachedPositionData({
        entryPrice,
        indexToken,
        isLong,
        leverageWithPnl,
        leverageWithoutPnl,
        markPrice,
        pnlAfterFeesPercentage,
        pnlAfterFeesUsd,
      });
    }
  }, [
    isPositionShareModalOpen,
    entryPrice,
    indexToken,
    isLong,
    leverageWithPnl,
    leverageWithoutPnl,
    markPrice,
    pnlAfterFeesPercentage,
    pnlAfterFeesUsd,
    prevIsOpen,
  ]);

  useEffect(() => {
    if (!prevIsOpen && isPositionShareModalOpen && shareSource === "auto-prompt") {
      userAnalytics.pushEvent<SharePositionActionEvent>({
        event: "SharePositionAction",
        data: {
          action: "PromptShown",
          source: "auto-prompt",
          hasReferralCode: hasReferralCode,
        },
      });
    }
  }, [hasReferralCode, isPositionShareModalOpen, prevIsOpen, shareSource]);

  useEffect(() => {
    if (userAffiliateCode.code) {
      setCreatedReferralCode(null);
    }
  }, [userAffiliateCode.code]);

  useEffect(() => {
    if (prevIsOpen && !isPositionShareModalOpen) {
      setPromptedToCreateReferralCode(false);
    }
  }, [prevIsOpen, isPositionShareModalOpen]);

  useEffect(() => {
    if (prevIsOpen && !isPositionShareModalOpen && shareSource === "auto-prompt") {
      userAnalytics.pushEvent<SharePositionActionEvent>({
        event: "SharePositionAction",
        data: {
          action: "PromptClose",
          source: shareSource,
          hasReferralCode: hasReferralCode,
          doNotShowAgain,
        },
      });
    }
  }, [prevIsOpen, isPositionShareModalOpen, hasReferralCode, shareSource, doNotShowAgain]);

  const uploadAndGetShareUrl = useCallback(async (): Promise<string | undefined> => {
    const element = cardRef.current;
    if (!element) return undefined;

    setIsUploading(true);
    setUploadError(null);
    element.classList.add("image-capture-in-progress");
    try {
      const imageInfo = await uploadElementAsShareImage(element);
      const ref = shareAffiliateCode.success && shareAffiliateCode.code ? shareAffiliateCode.code : undefined;
      return getShareURL(imageInfo.id, ref);
    } catch {
      setUploadError(t`Image generation failed. Refresh and try again.`);
      return undefined;
    } finally {
      element.classList.remove("image-capture-in-progress");
      setIsUploading(false);
    }
  }, [shareAffiliateCode]);

  const shouldShowCreateReferralCard = userAffiliateCode.success && !userAffiliateCode.code && !createdReferralCode;
  const handleReferralCodeSuccess = useCallback(
    (code: string) => {
      setCreatedReferralCode(code);

      userAnalytics.pushEvent<SharePositionActionEvent>({
        event: "SharePositionAction",
        data: {
          action: "ReferralCodeCreated",
          source: shareSource,
          hasReferralCode: true,
        },
      });
    },
    [shareSource]
  );
  async function handleCopyImage() {
    const element = cardRef.current;
    onShareAction?.();
    if (!element) return;
    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: {
        action: isMobile ? "ShareImage" : "CopyImage",
        source: shareSource,
        hasReferralCode: hasReferralCode,
      },
    });

    await shareOrCopyElementAsImage({ element, isMobile, fileName: "GMX Position.png" });
  }

  async function handleCopy() {
    onShareAction?.();

    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: {
        action: "Copy",
        source: shareSource,
        hasReferralCode: hasReferralCode,
      },
    });

    const url = await uploadAndGetShareUrl();
    if (url) {
      copyToClipboard(url);
      helperToast.success(t`Link copied to clipboard`);
    }
  }

  const handleShareTwitter = useCallback(async () => {
    onShareAction?.();
    userAnalytics.pushEvent<SharePositionActionEvent>(
      {
        event: "SharePositionAction",
        data: {
          action: "ShareTwitter",
          source: shareSource,
          hasReferralCode: hasReferralCode,
        },
      },
      { instantSend: true }
    );

    const url = await uploadAndGetShareUrl();
    const tweetLink = getTwitterIntentURL(`Latest $\u200a${indexToken?.symbol} trade on @GMX_IO`, url);
    window.open(tweetLink, "_blank", "noopener,noreferrer");
  }, [hasReferralCode, indexToken?.symbol, onShareAction, shareSource, uploadAndGetShareUrl]);

  const handleDoNotShowAgainToggle = useCallback(
    (value: boolean) => {
      onDoNotShowAgainChange?.(value);
    },
    [onDoNotShowAgainChange]
  );

  const handlePromptToCreateReferralCode = (e: React.MouseEvent<unknown>) => {
    e.preventDefault();
    setPromptedToCreateReferralCode(true);
  };

  const shouldPromptToCreateReferralCode =
    !hasReferralCode && !promptedToCreateReferralCode && !isCreateReferralCodeInfoMessageClosed;

  return (
    <ModalWithPortal
      contentClassName="md:!max-w-[500px]"
      isVisible={isPositionShareModalOpen}
      setIsVisible={setIsPositionShareModalOpen}
      label={t`Share your GMX trade on X`}
      contentPadding={false}
      withMobileBottomPosition
    >
      <div className="flex flex-col gap-20 border-b-1/2 border-slate-600 p-20">
        <div className="flex justify-center">
          {cachedPositionData && (
            <PositionShareCard
              entryPrice={cachedPositionData.entryPrice}
              indexToken={cachedPositionData.indexToken}
              isLong={cachedPositionData.isLong}
              leverage={isPnlInLeverage ? cachedPositionData.leverageWithPnl : cachedPositionData.leverageWithoutPnl}
              markPrice={cachedPositionData.markPrice}
              pnlAfterFeesPercentage={cachedPositionData.pnlAfterFeesPercentage}
              pnlAfterFeesUsd={cachedPositionData.pnlAfterFeesUsd}
              referralCodeOwnerKind={referralCodeOwnerKind}
              code={code}
              ref={cardRef}
              loading={isUploading}
              sharePositionBgImg={sharePositionBgImg}
              showPnlAmounts={showPnlAmounts}
            />
          )}
        </div>
        {shouldShowCreateReferralCard && <CreateReferralCode onSuccess={handleReferralCodeSuccess} />}
      </div>
      <div className="flex flex-col gap-12 p-20 pb-0">
        <ToggleSwitch isChecked={showPnlAmounts} setIsChecked={setShowPnlAmounts}>
          <span className="text-14 font-medium text-typography-secondary">
            {isRpnl ? <Trans>Show rPnL amounts</Trans> : <Trans>Show PnL amounts</Trans>}
          </span>
        </ToggleSwitch>

        <ToggleSwitch isChecked={isPnlInLeverage} setIsChecked={setIsPnlInLeverage}>
          <span className="text-14 font-medium text-typography-secondary">
            {isRpnl ? <Trans>Include rPnL in leverage display</Trans> : <Trans>Include PnL in leverage display</Trans>}
          </span>
        </ToggleSwitch>

        {promptedToCreateReferralCode && !isCreateReferralCodeInfoMessageClosed && (
          <ColorfulBanner color="blue" icon={InfoIcon} onClose={() => setIsCreateReferralCodeInfoMessageClosed(true)}>
            <div className="flex flex-col gap-4">
              <span className="font-medium text-blue-300">
                <Trans>Skip creating a referral code?</Trans>
              </span>
              <span className="text-blue-100">
                <Trans>Earn rewards by sharing your referral code</Trans>
              </span>
            </div>
          </ColorfulBanner>
        )}
      </div>
      <div className="flex flex-col gap-16 p-20 pt-12">
        {uploadError && (
          <AlertInfoCard type="error" hideClose>
            {uploadError}
          </AlertInfoCard>
        )}
        <div className="flex gap-12">
          <Button
            variant="secondary"
            disabled={isUploading}
            onClick={shouldPromptToCreateReferralCode ? handlePromptToCreateReferralCode : handleCopy}
            size="medium"
            className="grow !text-14"
          >
            <Trans>Copy link</Trans>
            <CopyStrokeIcon className="size-16" />
          </Button>
          <Button
            variant="secondary"
            onClick={shouldPromptToCreateReferralCode ? handlePromptToCreateReferralCode : handleCopyImage}
            size="medium"
            className="grow !text-14"
          >
            {isMobile ? (
              <>
                <Trans>Share</Trans>
                <ShareArrowOutlineIcon className="size-16" />
              </>
            ) : (
              <>
                <Trans>Copy image</Trans>
                <CopyStrokeIcon className="size-16" />
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            disabled={isUploading}
            onClick={shouldPromptToCreateReferralCode ? handlePromptToCreateReferralCode : handleShareTwitter}
            size="medium"
            className="grow !text-14"
          >
            <Trans>Share on</Trans>
            <TwitterIcon className="size-16" />
          </Button>
        </div>
        {Boolean(onDoNotShowAgainChange) && (
          <div className="flex justify-center">
            <Checkbox isChecked={doNotShowAgain} setIsChecked={handleDoNotShowAgainToggle}>
              <span className="text-14 font-medium text-typography-secondary">
                <Trans>Don't show this again</Trans>
              </span>
            </Checkbox>
          </div>
        )}
      </div>
    </ModalWithPortal>
  );
}

export default PositionShare;
